import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { mockGames } from '../mock/mockData';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const GamesCarousel = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [sectionRef, isVisible] = useScrollAnimation(0.1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState('next');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = React.useRef(null);

  const sortedGames = [...mockGames].sort((a, b) => a.id - b.id);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;
  const duration = 6500; // kolik sekund má trvat 1 slide

  const startTimer = () => {
    clearInterval(intervalRef.current);
    let start = 0;
    const step = 100;

    intervalRef.current = setInterval(() => {
      start += step;
      setProgress((start / duration) * 100);

      if (start >= duration) {
        start = 0;
        setProgress(0);
        goToNext();
      }
    }, step);
  };

  const resetTimer = () => {
    clearInterval(intervalRef.current);
    setProgress(0);
    startTimer();
  };


  useEffect(() => {
    startTimer();
    return () => clearInterval(intervalRef.current);
  }, []);



  const goToSlide = (index) => {
    if (isTransitioning) return;
    setDirection(index > currentIndex ? 'next' : 'prev');
    setIsTransitioning(true);
    setCurrentIndex(index);
    resetTimer();
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const goToPrevious = () => {
    if (isTransitioning) return;
    setDirection('prev');
    setIsTransitioning(true);
    resetTimer();
    setCurrentIndex((prev) => (prev - 1 + sortedGames.length) % sortedGames.length);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const goToNext = () => {
    if (isTransitioning) return;
    setDirection('next');
    setIsTransitioning(true);
    resetTimer();
    setCurrentIndex((prev) => (prev + 1) % sortedGames.length);
    setTimeout(() => setIsTransitioning(false), 700);
  };


  // Touch handlers for swipe (mobile)
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsAutoPlaying(false);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }

    // Reset touch states
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Mouse handlers for drag (desktop)
  const onMouseDown = (e) => {
    setIsDragging(true);
    setTouchStart(e.clientX);
    setTouchEnd(null);
    setIsAutoPlaying(false);
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    setTouchEnd(e.clientX);
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }

    // Reset states
    setTouchStart(null);
    setTouchEnd(null);
  };

  const onMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setTouchStart(null);
      setTouchEnd(null);
    }
  };

  const currentGame = sortedGames[currentIndex];
  const title = language === 'cz' && currentGame.titleCz != null ? currentGame.titleCz : currentGame.title;
  const description = language === 'cz' ? currentGame.descriptionCz : currentGame.description;
  const genre = language === 'cz' ? currentGame.genreCz : currentGame.genre;
  const status = language === 'cz' ? currentGame.statusCz : currentGame.status;

  return (
    <section id="games" ref={sectionRef} className="py-20 bg-gradient-to-b from-black via-gray-900 to-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl sm:text-5xl font-bold pb-4 bg-gradient-to-r from-red-600 to-red-400 bg-clip-text text-transparent">
            {t('games.title')}
          </h2>
          <p className="text-gray-400 text-lg">{t('games.subtitle')}</p>
        </div>

        {/* Carousel */}
        <div className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '200ms' }}>
          {/* Main Image and Content */}
          <div
            className={`relative h-[500px] md:h-[600px] rounded-2xl overflow-hidden group select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
          >
            {/* Background Image with Smooth Slide Animation */}
            <div
              key={currentIndex}
              className={`absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out ${direction === 'next' ? 'animate-slideInRight' : 'animate-slideInLeft'
                }`}
              style={{ backgroundImage: `url(${currentGame.thumbnail})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
            </div>

            {/* Content Overlay with Staggered Animation */}
            <div className="relative h-full flex flex-col justify-end p-6 md:p-12 pb-16 md:pb-12 z-10">
              <div key={`content-${currentIndex}`} className="max-w-2xl mb-8 md:mb-0 px-4 md:px-12">
                {/* Status Badge */}
                <div className="inline-block px-4 py-1 bg-red-600/90 backdrop-blur-sm rounded-full text-white text-sm font-semibold mb-4 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
                  {status}
                </div>

                {/* Title */}
                <h3 className="text-2xl md:text-5xl font-bold text-white mb-3 md:mb-4 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                  {title}
                </h3>

                {/* Genre */}
                <p className="text-red-400 text-xs md:text-sm uppercase tracking-wider font-semibold mb-2 md:mb-3 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
                  {genre} • {currentGame.platform}
                </p>

                {/* Description */}
                <p
                  className="text-gray-300 text-sm md:text-lg mb-4 md:mb-6 leading-relaxed animate-fadeInUp overflow-hidden text-ellipsis"
                  style={{
                    animationDelay: '0.4s',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,       // počet řádků, které se zobrazí
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {description}
                </p>


                {/* View Details Button */}
                <button
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate(`/game/${currentGame.gamePath}`);
                  }}
                  className="inline-flex items-center space-x-2 px-4 py-2 md:px-6 md:py-3 bg-red-600 hover:bg-red-700 text-white text-sm md:text-base font-semibold rounded-lg transition-all hover:scale-105 animate-fadeInUp"
                  style={{ animationDelay: '0.5s' }}
                >
                  <span>{t('games.viewDetails')}</span>
                  <ExternalLink size={16} className="md:w-[18px] md:h-[18px]" />
                </button>
              </div>
            </div>

            {/* Navigation Arrows - Only on desktop, centered vertically */}
            <button
              onClick={goToPrevious}
              disabled={isTransitioning}
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 hover:bg-red-900/70 backdrop-blur-sm items-center justify-center text-white transition-all hover:scale-110 z-20 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              aria-label="Previous game"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goToNext}
              disabled={isTransitioning}
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 hover:bg-red-900/70 backdrop-blur-sm items-center justify-center text-white transition-all hover:scale-110 z-20 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              aria-label="Next game"
            >
              <ChevronRight size={24} />
            </button>

            {/* Swipe Indicator for Mobile - Subtle hint */}
            <div className="md:hidden absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-xs flex items-center space-x-2 animate-pulse z-20 bg-black/30 backdrop-blur-sm px-3 py-1 rounded-full">
              <ChevronLeft size={14} />
              <span className="font-medium">Swipe</span>
              <ChevronRight size={14} />
            </div>
          </div>


          {/* Dots Indicator */}
          <div className="flex justify-center space-x-3 mt-6">
            {sortedGames.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`relative h-2 rounded-full transition-all overflow-hidden ${index === currentIndex
                  ? 'w-8 bg-red-600' // červená aktivní
                  : 'w-2 bg-gray-600 hover:bg-gray-500'
                  }`}
                aria-label={`Go to slide ${index + 1}`}
              >
                {/* Overlay maska jen v aktivní tečce */}
                {index === currentIndex && (
                  <div
                    className="absolute top-0 left-0 h-full bg-black/20 transition-[width] duration-100 linear"
                    style={{ width: `${progress}%` }}
                  ></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GamesCarousel;